/**
 * Sons da partida, sintetizados com Web Audio (nenhum arquivo para baixar).
 * O navegador só libera áudio depois de um clique na página; antes disso as chamadas são ignoradas sem erro.
 */

let ctx: AudioContext | null = null;
let ligado = true;

export function somLigado(): boolean {
  return ligado;
}

export function definirSom(v: boolean): void {
  ligado = v;
}

function contexto(): AudioContext | null {
  if (!ligado) return null;
  try {
    if (!ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
      carregarArquivos(ctx);
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx.state === 'running' || ctx.state === 'suspended' ? ctx : null;
  } catch {
    return null;
  }
}

// ---- sons do Brasfoot (public/sons/*.wav) ----------------------------------------------------------

type NomeSom = 'gol1' | 'goladv' | 'penalty' | 'expulsao' | 'contusao' | 'fimjogo' | 'intervalo';
const NOMES: NomeSom[] = ['gol1', 'goladv', 'penalty', 'expulsao', 'contusao', 'fimjogo', 'intervalo'];
const buffers = new Map<NomeSom, AudioBuffer | null>();
let carregando = false;

/** Baixa e decodifica os arquivos uma vez; arquivo que faltar vira null e o som sintetizado assume. */
function carregarArquivos(c: AudioContext): void {
  if (carregando) return;
  carregando = true;
  for (const nome of NOMES) {
    fetch(`./sons/${nome}.wav`)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error('sem arquivo'))))
      .then((dados) => c.decodeAudioData(dados))
      .then((b) => buffers.set(nome, b))
      .catch(() => buffers.set(nome, null));
  }
}

function tocar(c: AudioContext, nome: NomeSom, vol = 1): boolean {
  const b = buffers.get(nome);
  if (!b) return false;
  const fonte = c.createBufferSource();
  const g = c.createGain();
  g.gain.value = vol;
  fonte.buffer = b;
  fonte.connect(g).connect(c.destination);
  fonte.start();
  return true;
}

function nota(c: AudioContext, freq: number, ini: number, dur: number, vol: number, tipo: OscillatorType = 'triangle'): void {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = tipo;
  o.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, c.currentTime + ini);
  g.gain.exponentialRampToValueAtTime(vol, c.currentTime + ini + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + ini + dur);
  o.connect(g).connect(c.destination);
  o.start(c.currentTime + ini);
  o.stop(c.currentTime + ini + dur + 0.05);
}

/** Barulho de torcida: ruído filtrado que cresce e some. */
function torcida(c: AudioContext, dur: number, vol: number, freq: number): void {
  const n = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, n, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
  const fonte = c.createBufferSource();
  fonte.buffer = buf;
  const filtro = c.createBiquadFilter();
  filtro.type = 'bandpass';
  filtro.frequency.value = freq;
  filtro.Q.value = 0.7;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, c.currentTime);
  g.gain.exponentialRampToValueAtTime(vol, c.currentTime + dur * 0.3);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
  fonte.connect(filtro).connect(g).connect(c.destination);
  fonte.start();
}

/** Gol do time do jogador: fanfarra subindo e torcida alta. */
export function somGolMarcado(): void {
  const c = contexto();
  if (!c) return;
  if (tocar(c, 'gol1')) return;
  torcida(c, 2.4, 0.5, 1400);
  [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => nota(c, f, i * 0.12, 0.35, 0.22));
  nota(c, 1046.5, 0.5, 0.8, 0.2);
}

/** Gol sofrido: notas descendo e torcida abafada. */
export function somGolSofrido(): void {
  const c = contexto();
  if (!c) return;
  if (tocar(c, 'goladv')) return;
  torcida(c, 1.6, 0.22, 500);
  [392, 349.23, 293.66, 220].forEach((f, i) => nota(c, f, i * 0.18, 0.4, 0.2, 'sawtooth'));
}

/** Apito do árbitro marcando o pênalti. */
export function somApito(): void {
  const c = contexto();
  if (!c) return;
  if (tocar(c, 'penalty')) return;
  nota(c, 2600, 0, 0.22, 0.16, 'square');
  nota(c, 2600, 0.3, 0.45, 0.16, 'square');
}

/** Batidas graves durante o suspense do pênalti. */
export function somSuspense(duracaoMs: number): void {
  const c = contexto();
  if (!c) return;
  const batidas = Math.max(2, Math.floor(duracaoMs / 650));
  for (let i = 0; i < batidas; i++) {
    nota(c, 70, i * 0.65, 0.25, 0.35, 'sine');
    nota(c, 60, i * 0.65 + 0.2, 0.2, 0.25, 'sine');
  }
}

/** "Plim" curto para avisos e mensagens do bate-papo. */
export function somAviso(): void {
  const c = contexto();
  if (!c) return;
  nota(c, 880, 0, 0.12, 0.08, 'sine');
  nota(c, 1318.5, 0.1, 0.18, 0.08, 'sine');
}

/** Sons soltos do Brasfoot sem equivalente sintetizado: se o arquivo faltar, ficam em silêncio. */
export function somExpulsao(): void {
  const c = contexto();
  if (c) tocar(c, 'expulsao');
}

export function somContusao(): void {
  const c = contexto();
  if (c) tocar(c, 'contusao');
}

export function somFimDeJogo(): void {
  const c = contexto();
  if (c) tocar(c, 'fimjogo');
}

export function somIntervalo(): void {
  const c = contexto();
  if (c) tocar(c, 'intervalo');
}

/** Subiu de nível: escala subindo com um brilho no final. */
export function somNivel(): void {
  const c = contexto();
  if (!c) return;
  [392, 523.25, 659.25, 783.99, 1046.5].forEach((f, i) => nota(c, f, i * 0.09, 0.3, 0.12, 'triangle'));
  nota(c, 1568, 0.5, 0.7, 0.07, 'sine');
  nota(c, 2093, 0.55, 0.6, 0.05, 'sine');
}

/** Conquista liberada: dois toques de sino. */
export function somConquista(): void {
  const c = contexto();
  if (!c) return;
  nota(c, 987.77, 0, 0.5, 0.11, 'sine');
  nota(c, 1318.51, 0.16, 0.8, 0.11, 'sine');
  nota(c, 1975.53, 0.16, 0.6, 0.04, 'sine');
}

/** Pênalti defendido ou para fora: "uuuh" da torcida. */
export function somPerdeu(): void {
  const c = contexto();
  if (!c) return;
  torcida(c, 1.1, 0.25, 700);
}
